import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { tokens } from '../../theme';
import {
  uploadFile,
  getMappingPreferences,
  saveMappingPreferences,
  validateMapping,
  getPreview,
  executeImport,
} from '../../services/importService';

const ContactImport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fieldMapping, setFieldMapping] = useState({});
  const [previewContacts, setPreviewContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importDialog, setImportDialog] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  const steps = ['Télécharger fichier', 'Mapper les champs', 'Aperçu & Confirmation'];
  
  // Available fields for mapping - organized by category
  const availableFields = [
    { value: 'skip', label: '-- Ignorer --' },
    
    // Required fields
    { value: 'firstName', label: 'Prénom *', required: true },
    { value: 'lastName', label: 'Nom *', required: true },
    { value: 'email', label: 'Email *', required: true },
    
    // Contact information
    { value: 'phone', label: 'Téléphone' },
    
    // Address fields
    { value: 'address', label: 'Adresse' },
    { value: 'city', label: 'Ville' },
    { value: 'postalCode', label: 'Code postal' },
    { value: 'country', label: 'Code pays' },
    { value: 'countryName', label: 'Nom du pays' },
    { value: 'state', label: 'État/Province' },
    
    // Professional information
    { value: 'company', label: 'Entreprise' },
    { value: 'jobTitle', label: 'Titre du poste' },
    { value: 'profession', label: 'Profession' },
    
    // Personal information
    { value: 'nationality', label: 'Nationalité' },
    { value: 'birthDate', label: 'Date de naissance (AAAA/MM/JJ)' },
    { value: 'gender', label: 'Genre' },
    { value: 'maritalStatus', label: 'État civil' },
    { value: 'kids', label: 'Enfants' },
    
    // Preferences
    { value: 'language', label: 'Langue' },
    { value: 'levelOfEducation', label: 'Niveau d\'éducation' },
    { value: 'levelOfSatisfaction', label: 'Niveau de satisfaction' },
    { value: 'formOfCommunication', label: 'Forme de communication' },
    { value: 'preferredChannel', label: 'Canal préféré' },
    
    // Customer information
    { value: 'subscriptionStatus', label: 'Statut de l\'abonnement' },
    { value: 'customerType', label: 'Type de client' },
    
    // Social media
    { value: 'facebookId', label: 'ID Facebook' },
    
    // Other
    { value: 'interests', label: 'Intérêts' },
  ];

  // Load saved mapping preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await getMappingPreferences();
        if (prefs.mapping && Object.keys(prefs.mapping).length > 0) {
          setFieldMapping(prefs.mapping);
        }
      } catch (err) {
        console.error('Error loading preferences:', err);
      }
    };
    loadPreferences();
  }, []);

  const handleNext = async () => {
    if (activeStep === 1) {
      // Validate mapping before proceeding to preview
      setLoading(true);
      try {
        const validation = await validateMapping(fieldMapping);
        if (!validation.valid) {
          setValidationErrors(validation.errors);
          setLoading(false);
          return;
        }
        
        // Get preview
        const previewData = await getPreview(fileId, fieldMapping);
        setPreviewContacts(previewData.contacts);
        setValidationErrors([]);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } catch (err) {
        const errorDetail = err.response?.data?.detail || 'Erreur lors de la validation';
        
        // If file not found, reset to step 0 to allow re-upload
        if (err.response?.status === 404) {
          setError(errorDetail + ' Veuillez télécharger à nouveau votre fichier.');
          setActiveStep(0);
          setFileId(null);
          setSelectedFile(null);
        } else {
          setError(errorDetail);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
    setValidationErrors([]);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setLoading(true);
      setError(null);
      
      try {
        const uploadData = await uploadFile(file);
        setFileId(uploadData.file_id);
        setCsvHeaders(uploadData.headers);
        setTotalRows(uploadData.total_rows);
        
        // Initialize mapping with existing preferences or empty
        const initialMapping = {};
        uploadData.headers.forEach(header => {
          initialMapping[header] = fieldMapping[header] || 'skip';
        });
        setFieldMapping(initialMapping);
        
        // Auto-advance to step 2
        setActiveStep(1);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors du téléchargement du fichier');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMappingChange = async (csvColumn, profileField) => {
    const newMapping = {
      ...fieldMapping,
      [csvColumn]: profileField,
    };
    setFieldMapping(newMapping);
    
    // Save preferences
    try {
      await saveMappingPreferences(newMapping);
    } catch (err) {
      console.error('Error saving preferences:', err);
    }
  };

  const handleConfirm = async () => {
    setImportDialog(true);
    setLoading(true);
    setError(null);
    
    try {
      const results = await executeImport(fileId, fieldMapping);
      setImportResults(results);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Erreur lors de l\'importation';
      
      // If file not found, close dialog and reset to step 0
      if (err.response?.status === 404) {
        setImportDialog(false);
        setError(errorDetail + ' Veuillez télécharger à nouveau votre fichier.');
        setActiveStep(0);
        setFileId(null);
        setSelectedFile(null);
      } else {
        setError(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialog(false);
    if (importResults && importResults.success_count > 0) {
      navigate('/contacts');
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: colors.grey[100], minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={600} color={colors.primary[500]}>
          Importer des contacts
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contacts')}
          sx={{
            borderColor: colors.grey[400],
            color: colors.grey[700],
            '&:hover': {
              borderColor: colors.grey[600],
              backgroundColor: colors.grey[200],
            }
          }}
        >
          Retour
        </Button>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-active': {
                      color: colors.primary[500],
                    },
                    '&.Mui-completed': {
                      color: colors.primary[500],
                    },
                  }
                }}
              >
                <Typography variant="body2" fontWeight={activeStep === index ? 600 : 400}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Paper sx={{ p: 4, borderRadius: 2, minHeight: 400 }}>
        {/* Step 1: Upload File */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 1 : Importer votre fichier
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Téléchargez un fichier CSV contenant vos contacts. Les formats supportés incluent :{' '}
              <Box component="span" fontWeight={500}>
                Nom, Email, Téléphone, Entreprise...
              </Box>
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={2}>
                  Traitement du fichier...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `2px dashed ${colors.grey[400]}`,
                  borderRadius: 2,
                  p: 6,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: selectedFile ? alpha(colors.primary[100], 0.3) : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.grey[100],
                    borderColor: colors.primary[500],
                  }
                }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  type="file"
                  id="file-upload"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <CloudUploadIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: selectedFile ? colors.primary[500] : colors.grey[500],
                    mb: 2 
                  }} 
                />
                <Typography 
                  variant="body1" 
                  fontWeight={600} 
                  color={selectedFile ? colors.primary[500] : colors.primary[600]}
                  sx={{ cursor: 'pointer' }}
                >
                  {selectedFile ? `📄 ${selectedFile.name}` : '📂 Cliquez ici pour sélectionner un fichier CSV'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  ou glissez-déposez votre fichier
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Field Mapping */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 2 : Mapper les champs
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Associez les colonnes de votre fichier CSV aux champs de Qilinsa Amplify.
            </Typography>
            <Typography variant="body2" color="error" mb={2}>
              * Champs obligatoires
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Pour chaque colonne de votre fichier CSV, sélectionnez le champ de destination correspondant. 
              Seuls les trois champs marqués d'un astérisque (*) sont obligatoires. 
              Tous les autres champs sont optionnels.
            </Alert>

            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {validationErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 400, overflowY: 'auto', pr: 2 }}>
              {csvHeaders.map((header, index) => (
                <Box key={`mapping-${index}-${header}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.primary" sx={{ minWidth: 200 }}>
                    Colonne "{header}"
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Champ de destination
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 250 }} variant="outlined">
                      <Select
                        id={`mapping-select-${index}`}
                        value={fieldMapping[header] || 'skip'}
                        onChange={(e) => handleMappingChange(header, e.target.value)}
                        displayEmpty
                      >
                        {availableFields.map((field) => (
                          <MenuItem 
                            key={`${index}-${field.value}`} 
                            value={field.value}
                            sx={{
                              fontWeight: field.required ? 600 : 400,
                              color: field.required ? colors.primary[500] : 'inherit'
                            }}
                          >
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderColor: colors.grey[400],
                  color: colors.grey[700],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.grey[600],
                    backgroundColor: colors.grey[200],
                  }
                }}
              >
                Précédent
              </Button>
              <Button
                variant="contained"
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                onClick={handleNext}
                disabled={loading}
                sx={{
                  backgroundColor: colors.primary[500],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                  }
                }}
              >
                {loading ? 'Validation...' : 'Suivant'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Preview & Confirmation */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 3 : Aperçu & Confirmation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Voici un aperçu des premiers contacts importés. Vérifiez avant de confirmer.
            </Typography>
            <Typography variant="body2" fontWeight={600} color={colors.primary[500]} mb={4}>
              Total de contacts à importer : {totalRows}
            </Typography>

            <TableContainer sx={{ mb: 4, maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Prénom
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Nom
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Téléphone
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewContacts.map((contact, index) => (
                    <TableRow key={index}>
                      <TableCell>{contact.firstName || '-'}</TableCell>
                      <TableCell>{contact.lastName || '-'}</TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>{contact.phone || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mb: 4 }}>
              Les contacts en double (même email) seront automatiquement ignorés lors de l'importation.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderColor: colors.grey[400],
                  color: colors.grey[700],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.grey[600],
                    backgroundColor: colors.grey[200],
                  }
                }}
              >
                Précédent
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleConfirm}
                sx={{
                  backgroundColor: '#4caf50',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#45a049',
                  }
                }}
              >
                Confirmer l'import
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Import Progress Dialog */}
      <Dialog
        open={importDialog}
        onClose={loading ? undefined : handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {loading ? 'Importation en cours...' : 'Résultats de l\'importation'}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" color="text.secondary" mt={3}>
                Importation des contacts en cours...
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Veuillez patienter, cela peut prendre quelques instants.
              </Typography>
            </Box>
          ) : importResults ? (
            <Box sx={{ py: 2 }}>
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (
                <>
                  {importResults.success_count > 0 ? (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      Importation terminée avec succès !
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      Échec de l'importation - Aucun contact n'a été importé
                    </Alert>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      <strong>✅ Contacts importés :</strong> {importResults.success_count}
                    </Typography>
                  </Box>
                  
                  {importResults.skipped_count > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" color="warning.main">
                        <strong>⚠️ Contacts ignorés (doublons) :</strong> {importResults.skipped_count}
                      </Typography>
                      {importResults.duplicates && importResults.duplicates.length > 0 && (
                        <Box sx={{ mt: 1, pl: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Emails déjà existants :
                          </Typography>
                          {importResults.duplicates.map((email, idx) => (
                            <Typography key={idx} variant="body2" color="text.secondary">
                              - {email}
                            </Typography>
                          ))}
                          {importResults.skipped_count > importResults.duplicates.length && (
                            <Typography variant="body2" color="text.secondary">
                              ... et {importResults.skipped_count - importResults.duplicates.length} autres
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {importResults.error_count > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" color="error">
                        <strong>❌ Erreurs :</strong> {importResults.error_count}
                      </Typography>
                      {importResults.errors && importResults.errors.length > 0 && (
                        <Box sx={{ mt: 1, pl: 2 }}>
                          {importResults.errors.map((err, idx) => (
                            <Typography key={idx} variant="body2" color="error">
                              Ligne {err.row}: {err.error}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImportDialog}
            variant="contained"
            disabled={loading}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
            }}
          >
            {importResults && importResults.success_count > 0 ? 'Voir les contacts' : 'Fermer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactImport;

